import { Injectable } from '@angular/core';

/**
 * PersistencyService
 *
 * Stores non-sensitive session identifiers.
 *
 * The account IDs are kept in localStorage so their lifetime matches the
 * persistent HttpOnly refresh token cookie (30 days). sessionStorage is
 * tab-scoped and cleared on tab/window close, which left a reopened or new
 * tab authenticated (via the refresh cookie) but with no account IDs, causing
 * tasks to be created with a null createdBy/schoolAccountId.
 *
 * All access is guarded: on managed/locked-down browsers (common in schools)
 * the admin may block site storage, in which case getItem/setItem throw. We
 * swallow those errors so the app degrades to an in-memory-only session
 * (login still works for the current tab, just without persistence) instead
 * of crashing the login flow.
 *
 * The JWT access token is intentionally NOT stored here. It lives exclusively in
 * the NgRx in-memory store to prevent XSS token theft (OWASP A03).
 * Session persistence across page refreshes is achieved via the HttpOnly refresh
 * token cookie handled transparently by the browser.
 */
@Injectable({
  providedIn: 'root'
})
export class PersistencyService {
  private readonly USER_ACCOUNT_ID = 'UserAccountId';
  private readonly SCHOOL_ACCOUNT_ID = 'SchoolAccountId';
  private readonly IDENTIFIER = 'Identifier';

  constructor() { }

  getUserAccountId(): string {
    return this.safeGet(localStorage, this.USER_ACCOUNT_ID);
  }

  saveUserAccountId(userAccountId: string): void {
    this.safeSet(localStorage, this.USER_ACCOUNT_ID, userAccountId);
  }

  removeUserAccountId(): void {
    this.safeRemove(localStorage, this.USER_ACCOUNT_ID);
  }

  getSchoolAccountId(): string {
    return this.safeGet(localStorage, this.SCHOOL_ACCOUNT_ID);
  }

  saveSchoolAccountId(userAccountId: string): void {
    this.safeSet(localStorage, this.SCHOOL_ACCOUNT_ID, userAccountId);
  }

  removeSchoolAccountId(): void {
    this.safeRemove(localStorage, this.SCHOOL_ACCOUNT_ID);
  }

  getIdentifier(): string {
    return this.safeGet(sessionStorage, this.IDENTIFIER);
  }

  saveIdentifier(identifer: string): void {
    this.safeSet(sessionStorage, this.IDENTIFIER, identifer);
  }

  removeIdentifer(): void {
    this.safeRemove(sessionStorage, this.IDENTIFIER);
  }

  private safeGet(storage: Storage, key: string): string | null {
    try {
      return storage.getItem(key);
    } catch {
      // Storage blocked by browser policy — treat as not set.
      return null;
    }
  }

  private safeSet(storage: Storage, key: string, value: string): void {
    try {
      storage.setItem(key, value);
    } catch {
      // Storage blocked by browser policy — degrade to in-memory-only session.
      console.warn(`PersistencyService: unable to persist "${key}"; storage is unavailable.`);
    }
  }

  private safeRemove(storage: Storage, key: string): void {
    try {
      storage.removeItem(key);
    } catch {
      // Storage blocked by browser policy — nothing to clear.
    }
  }
}
